/**
 * GrantTenantAccessModal Component
 * Modal for granting cross-organization tenant access to users.
 *
 * Uses domain-based routing via analyze-upn endpoint to determine:
 * - can_grant: User exists in admin's org, grant directly
 * - create_in_org: Create new user in admin's org and grant
 * - can_grant_cross_org: User exists in another org, grant cross-org access
 * - other_org_no_user: Domain belongs to org but user doesn't exist (error)
 * - create_tenant_member: Unknown domain, create as tenant member
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Label,
  Spinner,
  Dropdown,
  Option,
  makeStyles,
  tokens,
  Text,
  Card,
  Avatar,
} from '@fluentui/react-components';
import { CheckmarkCircle20Regular, Warning20Regular } from '@fluentui/react-icons';
import { Alert } from '../common/Alert';
import { TenantApiService } from '../../services/tenantApi';
import type { AnalyzeUpnResponse } from '../../types/tenant';
import { MATTER_ROLE_OPTIONS } from './matterRoles';

export interface GrantTenantAccessModalProps {
  isOpen: boolean;
  tenantId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  confirmationCard: {
    marginTop: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  errorCard: {
    marginTop: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  licenseInfo: {
    marginTop: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  validationError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  userCard: {
    marginTop: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  userCardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  userInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  actionText: {
    marginTop: tokens.spacingVerticalXS,
  },
});

type ModalState = 'input' | 'analyzing' | 'confirmation' | 'error' | 'submitting';

export const GrantTenantAccessModal: React.FC<GrantTenantAccessModalProps> = ({
  isOpen,
  tenantId,
  onClose,
  onSuccess,
}) => {
  const styles = useStyles();

  const [upnInput, setUpnInput] = useState('');
  const [upnError, setUpnError] = useState<string | null>(null);
  const [modalState, setModalState] = useState<ModalState>('input');
  const [analyzeResult, setAnalyzeResult] = useState<AnalyzeUpnResponse | null>(null);
  const [matterRole, setMatterRole] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setUpnInput('');
      setUpnError(null);
      setModalState('input');
      setAnalyzeResult(null);
      setMatterRole('');
      setError(null);
    }
  }, [isOpen]);

  const validateUpn = useCallback((upn: string): boolean => {
    if (!upn.includes('@')) {
      setUpnError('UPN must contain @');
      return false;
    }
    setUpnError(null);
    return true;
  }, []);

  const handleGrant = useCallback(async () => {
    if (!validateUpn(upnInput)) {
      return;
    }

    setModalState('analyzing');
    setError(null);

    try {
      const result = await TenantApiService.analyzeUpn(tenantId, upnInput);
      setAnalyzeResult(result);

      // These statuses mean user doesn't exist - show error directing to Users tab
      if (result.status === 'other_org_no_user' ||
          result.status === 'create_in_org' ||
          result.status === 'create_tenant_member') {
        setModalState('error');
      } else {
        setModalState('confirmation');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze UPN';
      setError(errorMessage);
      setModalState('input');
    }
  }, [upnInput, tenantId, validateUpn]);

  const handleConfirm = useCallback(async () => {
    if (!analyzeResult) return;

    setModalState('submitting');
    setError(null);

    try {
      // For now, only handle can_grant case (existing user)
      // TODO: Handle create_in_org, can_grant_cross_org, create_tenant_member
      if (analyzeResult.status === 'can_grant' && analyzeResult.user) {
        await TenantApiService.grantUserTenantAccess(
          tenantId,
          analyzeResult.user.id,
          matterRole || undefined
        );
        onSuccess();
        onClose();
      } else if (analyzeResult.status === 'can_grant_cross_org' && analyzeResult.user) {
        await TenantApiService.grantUserTenantAccess(
          tenantId,
          analyzeResult.user.id,
          matterRole || undefined
        );
        onSuccess();
        onClose();
      } else {
        // TODO: Implement create user flows
        setError('This action is not yet implemented');
        setModalState('confirmation');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to grant access';
      setError(errorMessage);
      setModalState('confirmation');
    }
  }, [tenantId, analyzeResult, matterRole, onSuccess, onClose]);

  const renderConfirmationContent = () => {
    if (!analyzeResult) return null;

    const { status, user, other_org_name, license_info } = analyzeResult;
    const licenseText = license_info.org_cap
      ? `${license_info.org_used}/${license_info.org_cap} licenses used`
      : `${license_info.org_used} licenses used`;

    switch (status) {
      case 'can_grant':
        return (
          <Card className={styles.userCard}>
            <div className={styles.userCardContent}>
              <div className={styles.statusRow}>
                <CheckmarkCircle20Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                <Text weight="semibold">Existing User Found</Text>
              </div>
              <div className={styles.userInfoRow}>
                <Avatar name={user?.name} size={32} color="colorful" />
                <div className={styles.userDetails}>
                  <Text weight="semibold" style={{ color: tokens.colorBrandBackgroundSelected, lineHeight: '1.2' }}>
                    {user?.name}
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, lineHeight: '1.2' }}>
                    {user?.email}
                  </Text>
                </div>
              </div>
              <Text className={styles.actionText}>Grant access to this tenant?</Text>
              <Text className={styles.licenseInfo}>{licenseText}</Text>
            </div>
          </Card>
        );

      case 'can_grant_cross_org':
        return (
          <Card className={styles.userCard}>
            <div className={styles.userCardContent}>
              <div className={styles.statusRow}>
                <Text weight="semibold">Cross-Organization User</Text>
              </div>
              <div className={styles.userInfoRow}>
                <Avatar name={user?.name} size={32} color="colorful" />
                <div className={styles.userDetails}>
                  <Text weight="semibold" style={{ color: tokens.colorBrandBackgroundSelected, lineHeight: '1.2' }}>
                    {user?.name}
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, lineHeight: '1.2' }}>
                    {user?.email}
                  </Text>
                </div>
              </div>
              <Text className={styles.actionText}>
                This user belongs to <strong>{other_org_name}</strong>. Grant cross-org access?
              </Text>
              <Text className={styles.licenseInfo}>{licenseText}</Text>
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  const renderErrorContent = () => {
    if (!analyzeResult) return null;

    const { status, other_org_name } = analyzeResult;

    if (status === 'other_org_no_user') {
      return (
        <div className={styles.errorCard}>
          <div className={styles.statusRow}>
            <Warning20Regular style={{ color: tokens.colorPaletteRedForeground1 }} />
            <Text weight="semibold">Cannot Grant Access</Text>
          </div>
          <Text style={{ marginTop: tokens.spacingVerticalS, display: 'block' }}>
            The organization <strong>{other_org_name}</strong> uses IRPilot but hasn&apos;t created this user.
            Contact their admin to create the user first.
          </Text>
        </div>
      );
    }

    if (status === 'create_in_org' || status === 'create_tenant_member') {
      return (
        <div className={styles.errorCard}>
          <div className={styles.statusRow}>
            <Warning20Regular style={{ color: tokens.colorPaletteRedForeground1 }} />
            <Text weight="semibold">User Not Found</Text>
          </div>
          <Text style={{ marginTop: tokens.spacingVerticalS, display: 'block' }}>
            No user exists with this email. Please create the user first in the <strong>Users</strong> tab.
          </Text>
        </div>
      );
    }

    return null;
  };

  if (!isOpen) {
    return null;
  }

  const isAnalyzing = modalState === 'analyzing';
  const isSubmitting = modalState === 'submitting';
  const showConfirmation = modalState === 'confirmation' || modalState === 'submitting';
  const showError = modalState === 'error';

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Grant Tenant Access</DialogTitle>
          <DialogContent>
            {error && (
              <Alert intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
                {error}
              </Alert>
            )}

            <div className={styles.form}>
              <div className={styles.field}>
                <Label htmlFor="upn-input" required>
                  Email
                </Label>
                <Input
                  id="upn-input"
                  aria-label="Email"
                  placeholder="user@example.com"
                  value={upnInput}
                  onChange={(e) => {
                    setUpnInput(e.target.value);
                    setUpnError(null);
                    // Reset to input state when input changes
                    if (showConfirmation || showError) {
                      setModalState('input');
                      setAnalyzeResult(null);
                    }
                  }}
                  disabled={isAnalyzing || isSubmitting}
                />
                {upnError && (
                  <Text className={styles.validationError}>{upnError}</Text>
                )}
              </div>

              {showConfirmation && renderConfirmationContent()}
              {showError && renderErrorContent()}

              <div className={styles.field}>
                <Label htmlFor="matter-role">Matter Role</Label>
                <Dropdown
                  id="matter-role"
                  aria-label="Matter Role"
                  placeholder="Select a role..."
                  value={MATTER_ROLE_OPTIONS.find(opt => opt.value === matterRole)?.label || ''}
                  selectedOptions={matterRole ? [matterRole] : []}
                  onOptionSelect={(_, data) => {
                    setMatterRole(data.optionValue || '');
                  }}
                >
                  {MATTER_ROLE_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Dropdown>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={isAnalyzing || isSubmitting}>
              Cancel
            </Button>
            {showConfirmation ? (
              <Button
                appearance="primary"
                onClick={handleConfirm}
                disabled={isSubmitting}
                icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
              >
                {isSubmitting ? 'Granting...' : 'Confirm'}
              </Button>
            ) : (
              <Button
                appearance="primary"
                onClick={handleGrant}
                disabled={!upnInput || isAnalyzing || showError}
                icon={isAnalyzing ? <Spinner size="tiny" /> : undefined}
              >
                {isAnalyzing ? 'Analyzing...' : 'Grant'}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
